import styles from './shareTeamDrawer.module.scss';
import Drawer from '@mui/material/Drawer';
import { useState, useMemo, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { TextField, InputAdornment, Button, Autocomplete } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import actions from 'store/actions';
import { updateNA, addSharedTeamMember, removeSharedTeamMember } from 'store/na/thunk';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import debounce from 'lodash/debounce';
import { getUsers } from 'api/users';
import { useTranslation } from 'react-i18next';
import { weakKey } from 'utils/objects';

const DRAWER_ANCHOR_TYPE = 'right';
const TEAM_MEMBERS = 'team_members_';
const defaultTeamMember = {
  id: 0,
  name: '',
  email: '',
};

export default function ShareTeamDrawer(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpen = useMemo(() => props.state, [props]);
  const currentUser = useSelector((state) => state.user);
  const sharedUsers = useSelector((state) => state.na.sharedUsers);
  const anyonecanaccess = useSelector((state) => state.na.anyonecanaccess);
  const owner = useSelector((state) => state.na.owner);

  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const [teamMemberLoading, setTeamMemberLoading] = useState(false);
  const [teamMember, setTeamMember] = useState(defaultTeamMember);
  const [teamMemberList, setTeamMemberList] = useState([]);
  const [autocompleteKey, setAutocompleteKey] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(false);

  const onChangeSelectedTeamMember = (selectedTeamMember) => {
    setTeamMember(selectedTeamMember);
    setAddButtonDisabled(!selectedTeamMember);
  };

  const onInputChangeTeamMember = useCallback(
    async (evt, value) => {
      setTeamMemberList([]);
      if (!value || evt.type !== 'change') return;

      setTeamMemberLoading(true);
      setOpenDropdown(true);
      const users = await getUsers(value);
      setTeamMemberLoading(false);

      const filteredUsers = users.filter((user) => user.id !== owner.id);

      setTeamMemberList(filteredUsers);
    },
    [owner],
  );
  const debouncedTeamMemberSearch = useMemo(
    () => debounce(onInputChangeTeamMember, 100),
    [onInputChangeTeamMember],
  );

  const addTeamMember = () => {
    setTeamMemberList([]);
    setTeamMember(defaultTeamMember);
    setAutocompleteKey(!autocompleteKey);
    setAddButtonDisabled(true);

    dispatch(addSharedTeamMember(teamMember));
  };

  const removeTeamMember = useCallback(
    (teamMemberToRemove) => {
      dispatch(removeSharedTeamMember(teamMemberToRemove));
    },
    [dispatch],
  );
  const debouncedRemoveTeamMember = useMemo(
    () => debounce(removeTeamMember, 200),
    [removeTeamMember],
  );

  const handleAnyoneCanAccess = useCallback(
    (checked) => {
      dispatch(actions.na.setAnyoneCanAccess(checked));
      dispatch(updateNA);
    },
    [dispatch],
  );
  const debouncedAnyoneCanAccess = useMemo(
    () => debounce(handleAnyoneCanAccess, 200),
    [handleAnyoneCanAccess],
  );

  const switchDisabled = useMemo(
    () => !(currentUser.isAdmin || owner.email?.toLowerCase() === currentUser.email?.toLowerCase()),
    [currentUser, owner],
  );

  const CustomSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme }) => ({
    width: 52,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(26px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#3a3e43',
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: '#33cf4d',
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }));

  return (
    <Drawer
      data-testid="drawer"
      anchor={DRAWER_ANCHOR_TYPE}
      open={isOpen}
      onClose={() => props.toggleDrawer(false)}>
      <div className={styles.content}>
        <div className={styles.main}>
          <div className={styles.titles}>
            <div className={styles.first_title}>{t('step6.team')}</div>
            <div className={styles.second_title}>{t('step6.manage')}</div>
          </div>
          <IconButton
            data-testid="ShareTeamDrawerCloseBtn"
            onClick={() => props.toggleDrawer(false)}
            className={styles.close}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.description}>
          <CustomSwitch
            disabled={switchDisabled}
            checked={anyonecanaccess}
            onClick={(event) => {
              debouncedAnyoneCanAccess(event.target.checked);
            }}
          />
          {t('step6.toggle')}
        </div>
        <div className={styles.search}>
          <div className={styles.title}>{t('step6.search')}</div>
          <div className={styles.search_row}>
            <Autocomplete
              data-testid="ShareTeamInput"
              disablePortal
              key={autocompleteKey}
              forcePopupIcon={openDropdown}
              open={openDropdown}
              onClose={() => setOpenDropdown(false)}
              onChange={(_, value) => onChangeSelectedTeamMember(value)}
              loading={teamMemberLoading}
              className={styles.input_box}
              options={teamMemberList}
              onInputChange={debouncedTeamMemberSearch}
              loadingText={t('common.loading')}
              noOptionsText={t('common.noOptions')}
              isOptionEqualToValue={(option, value) => {
                return option && value && option.name === value.name;
              }}
              getOptionLabel={(option) => option.name ?? ''}
              filterOptions={(value) => value}
              renderInput={(params) => {
                params.InputProps.startAdornment = (
                  <>
                    <InputAdornment position="start">
                      <SearchOutlinedIcon />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                );
                return <TextField {...params} placeholder={t('step6.searchTeamMember')} />;
              }}
              renderOption={(props, option, { inputValue }) => {
                const parts = option.name
                  .split(new RegExp('(' + inputValue + ')', 'i'))
                  .map((part) => ({
                    text: part,
                    highlight: part?.toLowerCase() === inputValue?.toLowerCase(),
                  }));

                return (
                  <li {...props}>
                    <div>
                      {parts.map((part) => (
                        <span
                          key={weakKey(part)}
                          style={{
                            fontWeight: part.highlight ? 800 : 400,
                          }}>
                          {part.text}
                        </span>
                      ))}
                    </div>
                  </li>
                );
              }}
            />
            <Button
              data-testid="ShareTeamAddBtn"
              className={styles.add_button}
              variant="contained"
              onClick={addTeamMember}
              disabled={addButtonDisabled}>
              {t('step6.add')}
            </Button>
          </div>
        </div>
        <div className={styles.members_list}>
          <div className={styles.name}>
            <PersonOutlineOutlinedIcon />
            {owner.name}
          </div>
          {sharedUsers &&
            sharedUsers.map(
              (member) =>
                member && (
                  <div key={TEAM_MEMBERS + member.id} className={styles.member_item}>
                    <div className={styles.name}>
                      <PersonOutlineOutlinedIcon />
                      {member.name}
                    </div>
                    <IconButton
                      data-testid="ShareTeamCloseBtn"
                      onClick={() => debouncedRemoveTeamMember(member)}
                      className={styles.remove}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </div>
                ),
            )}
        </div>
      </div>
    </Drawer>
  );
}
